import {Injectable, OnDestroy} from '@angular/core';
import {Observable, Subject} from 'rxjs';

/** The user did something in another tab (or clicked Stay there): every tab restarts its idle time. */
export interface SessionActivityMessage {
  type: 'activity';
  /** When the activity happened, in ms since the epoch. */
  at: number;
}

/** Another tab's idle time ran out: every tab shows the "Session timeout" countdown, ending at the same moment. */
export interface SessionCountdownMessage {
  type: 'countdown';
  /** When the session signs out unless someone stays, in ms since the epoch. */
  logoutAtMs: number;
}

/** "Stay in Orbitax" was clicked in another tab: every tab closes its countdown and restarts its idle time. */
export interface SessionStayMessage {
  type: 'stay';
  /** When Stay was clicked, in ms since the epoch. */
  at: number;
}

export type SessionTabsMessage = SessionActivityMessage | SessionCountdownMessage | SessionStayMessage;

/**
 * The session timeout's signal between this browser's tabs (ITP2-14141): a BroadcastChannel named 'orbitax-session'.
 * One session is shared by every tab, so its idle time is too: activity in any tab, the countdown, and Stay reach all
 * of them. It is a shortcut, not the truth: the last activity is also kept in localStorage (SessionActivityStore),
 * which every tab checks before it shows a countdown or signs out, so a lost message never signs out an active tab.
 * A tab never hears its own posts. Where BroadcastChannel is missing it does nothing.
 */
@Injectable({providedIn: 'root'})
export class SessionTabsChannel implements OnDestroy {
  static readonly channelName = 'orbitax-session';

  private readonly channel: BroadcastChannel | null = SessionTabsChannel.open();
  private readonly incoming = new Subject<SessionTabsMessage>();
  /** What the other tabs of this browser posted. */
  readonly messages$: Observable<SessionTabsMessage> = this.incoming.asObservable();

  constructor() {
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent) => {
        const message = SessionTabsChannel.parse(event?.data);
        if (message) {
          this.incoming.next(message);
        }
      };
    }
  }

  /** Tells this browser's other tabs; a closed or missing channel drops it silently. */
  post(message: SessionTabsMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch { /* closed: the shared last activity still stands */ }
  }

  ngOnDestroy(): void {
    this.incoming.complete();
    this.channel?.close();
  }

  /** The message when it is one of ours with a usable time, else null. */
  static parse(data: any): SessionTabsMessage | null {
    if (!data || typeof data !== 'object') {
      return null;
    }
    if ((data.type === 'activity' || data.type === 'stay') && Number.isFinite(data.at)) {
      return {type: data.type, at: data.at};
    }
    if (data.type === 'countdown' && Number.isFinite(data.logoutAtMs)) {
      return {type: 'countdown', logoutAtMs: data.logoutAtMs};
    }
    return null;
  }

  private static open(): BroadcastChannel | null {
    try {
      return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SessionTabsChannel.channelName);
    } catch {
      return null;
    }
  }
}
